import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import FarmersNav from "@/components/FarmersNav";
import { fpPath } from "@/lib/site";
import {
  ChatCircleDots, Heart, Image as ImageIcon, PaperPlaneTilt, X, ArrowLeft,
  Sparkle, UserCircle, DotsThree, TrashSimple,
} from "@phosphor-icons/react";

/**
 * FarmersForumPage — /farmers/forum · Facebook-style discussion feed.
 * Composer at the top (text + optional photo), infinite-ish list of
 * posts with like button, expandable comments, and author-only delete.
 * Photo uploads flow through the existing /api/uploads/photo endpoint.
 */

const MAX_PHOTO_MB = 8;

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function FarmersForumPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/farmers/forum/posts?limit=40");
      setPosts(r.data.posts || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Could not load the forum.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreated = (post) => {
    setPosts((p) => [post, ...p]);
    setTotal((t) => t + 1);
  };

  const onDeleted = (id) => {
    setPosts((p) => p.filter((x) => x.id !== id));
    setTotal((t) => Math.max(0, t - 1));
  };

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="farmers-forum-page">
      <div className="max-w-3xl mx-auto"><FarmersNav /></div>

      <div className="max-w-3xl mx-auto mt-6">
        <Link
          to={fpPath("/")}
          className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          data-testid="forum-back"
        >
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>

        <header className="mt-3">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <ChatCircleDots size={12} weight="fill" className="inline mr-1" /> Farmers Forum
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-[#151312]">
            Ask a farmer. Share a photo. Trade a tip.
          </h1>
          <p className="mt-2 text-[#3A3830]">
            A neighborhood-style feed for growers, gardeners and market-goers. Post a photo,
            ask a question, or answer someone else's — every like helps a good tip travel.
          </p>
        </header>

        <Composer onCreated={onCreated} />

        <div className="mt-6 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#6C685E]">
          {loading ? "Loading…" : `${total} post${total === 1 ? "" : "s"}`}
        </div>

        {loading && (
          <div className="mt-4 space-y-4" data-testid="forum-loading">
            {[0,1,2].map((i) => <div key={i} className="h-40 rounded-2xl bg-black/5 animate-pulse" />)}
          </div>
        )}

        {!loading && posts.length === 0 && (
          <div className="mt-6 rounded-2xl border border-dashed border-black/20 bg-white/60 p-8 text-center" data-testid="forum-empty">
            <ChatCircleDots size={22} weight="duotone" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-2xl text-[#151312]">
              Be the first to post. Share a harvest, a question, or a photo.
            </div>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div className="mt-4 space-y-4" data-testid="forum-feed">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} onDeleted={onDeleted} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

// =====================================================================
//  Composer
// =====================================================================
function Composer({ onCreated }) {
  const [body, setBody] = useState("");
  const [crop, setCrop] = useState("");
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const clear = () => { setBody(""); setCrop(""); setPhotoUrl(null); };

  const pickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      toast.error(`Photo must be under ${MAX_PHOTO_MB} MB.`);
      return;
    }
    setUploading(true);
    try {
      const data = await readAsBase64(file);
      const r = await http.post("/uploads/photo", {
        image_base64: data,
        content_type: file.type || "image/jpeg",
      });
      setPhotoUrl(r.data.url);
    } catch (err) {
      toast.error("Photo upload failed. Try a smaller image.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) { toast.error("Write something first."); return; }
    setBusy(true);
    try {
      const r = await http.post("/farmers/forum/posts", {
        body: body.trim(),
        photo_url: photoUrl,
        crop: crop.trim() || null,
        topics: [],
      });
      onCreated(r.data.post);
      clear();
      toast.success("Posted.");
    } catch (err) {
      if (err?.response?.status === 401) toast.error("Please sign in to post.");
      else toast.error("Could not post — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="forum-composer"
      className="mt-6 rounded-3xl bg-white border border-black/10 p-4 md:p-5"
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Share a photo, ask a question, trade a tip…"
        rows={3}
        maxLength={4000}
        data-testid="forum-composer-body"
        className="w-full bg-transparent outline-none resize-none text-[15px] text-[#151312] placeholder:text-[#8A857A]"
      />

      {photoUrl && (
        <div className="relative mt-2 inline-block">
          <img
            src={photoUrl}
            alt="preview"
            className="max-h-64 rounded-xl border border-black/10"
            data-testid="forum-composer-preview"
          />
          <button
            type="button"
            onClick={() => setPhotoUrl(null)}
            className="absolute top-2 right-2 grid place-items-center h-7 w-7 rounded-full bg-[#151312]/80 text-white hover:bg-[#151312]"
            aria-label="Remove photo"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-black/10">
        <input
          type="text"
          value={crop}
          onChange={(e) => setCrop(e.target.value)}
          placeholder="Crop (optional)"
          maxLength={60}
          data-testid="forum-composer-crop"
          className="bg-[#F5EBDC] rounded-full px-3 py-1.5 text-xs text-[#151312] placeholder:text-[#8A857A] outline-none w-40"
        />
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="forum-composer-file" />
        <button
          type="button"
          onClick={pickFile}
          disabled={uploading}
          data-testid="forum-composer-photo-btn"
          className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] hover:border-[color:var(--np-brand,#B85F2A)] disabled:opacity-50"
        >
          <ImageIcon size={11} weight="duotone" /> {uploading ? "Uploading…" : "Photo"}
        </button>
        <button
          type="submit"
          disabled={busy || uploading || !body.trim()}
          data-testid="forum-composer-submit"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50 hover:opacity-90"
        >
          <PaperPlaneTilt size={11} weight="fill" /> Post
        </button>
      </div>
    </form>
  );
}

// =====================================================================
//  Post card
// =====================================================================
function timeAgo(iso) {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60)     return "just now";
    if (diff < 3600)   return `${Math.round(diff / 60)} min ago`;
    if (diff < 86400)  return `${Math.round(diff / 3600)} h ago`;
    if (diff < 604800) return `${Math.round(diff / 86400)} d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

function PostCard({ post, onDeleted }) {
  const [liked, setLiked] = useState(!!post.viewer_liked);
  const [likes, setLikes] = useState(post.likes_count || 0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleLike = async () => {
    // optimistic
    setLiked((v) => !v); setLikes((n) => n + (liked ? -1 : 1));
    try {
      const r = await http.post(`/farmers/forum/posts/${post.id}/like`);
      setLiked(r.data.liked); setLikes(r.data.likes_count);
    } catch (e) {
      // revert
      setLiked((v) => !v); setLikes((n) => n + (liked ? 1 : -1));
      if (e?.response?.status === 401) toast.error("Sign in to like posts.");
    }
  };

  const openComments = async () => {
    setCommentsOpen((v) => !v);
    if (commentsLoaded) return;
    try {
      const r = await http.get(`/farmers/forum/posts/${post.id}`);
      setComments(r.data.comments || []);
      setCommentsLoaded(true);
    } catch (e) {
      toast.error("Could not load comments.");
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentBody.trim()) return;
    setCommentBusy(true);
    try {
      const r = await http.post(`/farmers/forum/posts/${post.id}/comments`, { body: commentBody.trim() });
      setComments((c) => [...c, r.data.comment]);
      setCommentBody("");
    } catch (err) {
      if (err?.response?.status === 401) toast.error("Sign in to comment.");
      else toast.error("Could not post comment.");
    } finally {
      setCommentBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await http.delete(`/farmers/forum/posts/${post.id}`);
      onDeleted(post.id);
      toast.success("Post deleted.");
    } catch (e) {
      toast.error("Could not delete — are you the author?");
    }
  };

  return (
    <article
      data-testid={`forum-post-${post.id}`}
      className="rounded-3xl bg-white border border-black/10 p-4 md:p-5"
    >
      <header className="flex items-center gap-3">
        {post.author_photo ? (
          <img src={post.author_photo} alt="" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <UserCircle size={40} weight="duotone" className="text-[color:var(--np-brand,#B85F2A)]" />
        )}
        <div className="min-w-0">
          <div className="font-serif-display text-[15px] text-[#151312] leading-tight truncate">
            {post.author_name}
          </div>
          <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#6C685E]">
            {timeAgo(post.created_at)}
            {post.crop && <> · #{post.crop}</>}
          </div>
        </div>
        <div className="ml-auto relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid={`forum-post-menu-${post.id}`}
            className="grid place-items-center h-8 w-8 rounded-full hover:bg-black/5"
            aria-label="Post menu"
          >
            <DotsThree size={16} weight="bold" className="text-[#3A3830]" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-9 z-10 rounded-xl bg-white shadow-lg border border-black/10 py-1 w-40">
              <button
                onClick={() => { setMenuOpen(false); remove(); }}
                data-testid={`forum-post-delete-${post.id}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#3A3830] hover:bg-black/5"
              >
                <TrashSimple size={12} weight="fill" /> Delete (author only)
              </button>
            </div>
          )}
        </div>
      </header>

      {post.body && (
        <p className="mt-3 text-[15px] text-[#1F1D1A] whitespace-pre-wrap leading-snug">{post.body}</p>
      )}

      {post.photo_url && (
        <img
          src={post.photo_url}
          alt=""
          className="mt-3 w-full max-h-[560px] object-cover rounded-2xl border border-black/10"
          data-testid={`forum-post-photo-${post.id}`}
        />
      )}

      <div className="mt-3 pt-3 border-t border-black/10 flex items-center gap-4">
        <button
          onClick={toggleLike}
          data-testid={`forum-post-like-${post.id}`}
          className={`inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition ${
            liked ? "text-[color:var(--np-brand,#B85F2A)]" : "text-[#3A3830] hover:text-[color:var(--np-brand,#B85F2A)]"
          }`}
        >
          <Heart size={14} weight={liked ? "fill" : "regular"} /> {likes} {likes === 1 ? "like" : "likes"}
        </button>
        <button
          onClick={openComments}
          data-testid={`forum-post-comments-btn-${post.id}`}
          className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] hover:text-[color:var(--np-brand,#B85F2A)]"
        >
          <ChatCircleDots size={13} weight="duotone" /> {post.comments_count + (comments.length - (post.comments_count || 0))} comments
        </button>
      </div>

      {commentsOpen && (
        <div className="mt-3 pt-3 border-t border-black/10 space-y-3" data-testid={`forum-post-comments-${post.id}`}>
          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2">
              {c.author_photo ? (
                <img src={c.author_photo} alt="" className="h-7 w-7 rounded-full object-cover" />
              ) : (
                <UserCircle size={28} weight="duotone" className="text-[color:var(--np-brand,#B85F2A)] shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="rounded-2xl bg-[#F5EBDC] px-3 py-2">
                  <div className="font-serif-display text-[13px] text-[#151312] leading-tight">
                    {c.author_name}
                  </div>
                  <p className="mt-0.5 text-[13px] text-[#1F1D1A] leading-snug whitespace-pre-wrap">{c.body}</p>
                </div>
                <div className="mt-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#6C685E]">
                  {timeAgo(c.created_at)}
                </div>
              </div>
            </div>
          ))}
          <form onSubmit={submitComment} className="flex items-center gap-2">
            <input
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder="Write a comment…"
              maxLength={2000}
              data-testid={`forum-comment-input-${post.id}`}
              className="flex-1 bg-[#F5EBDC] rounded-full px-4 py-2 text-sm text-[#151312] placeholder:text-[#8A857A] outline-none"
            />
            <button
              type="submit"
              disabled={commentBusy || !commentBody.trim()}
              data-testid={`forum-comment-submit-${post.id}`}
              className="grid place-items-center h-9 w-9 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white disabled:opacity-50 hover:opacity-90"
              aria-label="Post comment"
            >
              <PaperPlaneTilt size={13} weight="fill" />
            </button>
          </form>
        </div>
      )}
    </article>
  );
}
